import { IIconProps } from '@fluentui/react';
import { FluentProvider, makeStyles, mergeClasses, webLightTheme } from '@fluentui/react-components';
import {
    ArrowClockwise20Regular,
    CalendarArrowCounterclockwise20Regular,
    Camera20Regular,
    Clock20Regular,
    Delete20Regular,
    Headset20Regular,
    Info20Regular,
    Laptop20Regular,
    Next20Regular,
    Open20Regular,
    Play20Regular,
    Power20Regular,
    TaskListLtr20Regular,
    Wrench20Regular,
} from '@fluentui/react-icons';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { HyperVDogfoodingInstructionsLink } from '../../../constants/add-ons';
import { DevBoxFailureReason } from '../../../constants/dev-box';
import {
    VSCodeConnectCommand,
    VSCodeDevBoxExtensionWebHookPrefix,
    VSCodeDevBoxUriParameter,
} from '../../../constants/dev-box-vscode-extension';
import { FeatureFlagName } from '../../../constants/features';
import { Metric, Property, Severity } from '../../../constants/telemetry';
import devBoxCardPreviewWin11Image_Dark from '../../../content/images/dev-box-preview-win11_dark.jpg';
import devBoxCardPreviewWin11Image_Light from '../../../content/images/dev-box-preview-win11_light.jpg';
import devBoxCardPreviewLinuxImage from '../../../content/images/linux.jpg';
import { useActionCreator } from '../../../hooks/action-creator';
import { useCurrentFluent2Theme } from '../../../hooks/styling';
import { useToast } from '../../../hooks/use-toast';
import LinuxDogfoodingLogoIcon from '../../../icons/linux-icon';
import { HibernateSupport } from '../../../models/pool';
import { dismissContent as dismissContentActionCreator } from '../../../redux/actions/application/application-action-creators';
import { getDevBoxTunnelPhaseStates } from '../../../redux/selector/add-ons-selectors';
import { getWelcomeTourSeenStatus } from '../../../redux/selector/application-selectors';
import {
    getRemoteAppOptionsSelector,
    getUseMultiMonitorSelector,
} from '../../../redux/selector/user-settings-selector';
import { DevBoxTunnelPhase } from '../../../redux/store/add-ons-store';
import { DevBoxState } from '../../../redux/store/dev-box-state';
import { useHorizontalStackStyles } from '../../../themes/styles/flexbox-styles';
import { RemoteAppOption } from '../../../types/user-settings';
import {
    findPrimaryTunnelAddon,
    getTunnelCanBeConnected,
    isUserSessionNotAvailableForTunnelCode,
} from '../../../utilities/add-ons';
import { openInNewWindow, openInSameWindow, operatingSystemFamily } from '../../../utilities/browser';
import {
    getLatestCustomizationGroupFailureEndTime,
    getOverallCustomizationGroupStatus,
    isCustomizationGroupsWaitingForUserSession,
    isFailedCustomizationGroupStatus,
} from '../../../utilities/customization-group';
import { isFeatureFlagEnabled } from '../../../utilities/features';
import { isNotUndefinedOrWhiteSpace } from '../../../utilities/string';
import { trackMetric, trackTrace } from '../../../utilities/telemetry/channel';
import { getTimeStringFromDate } from '../../../utilities/time';
import { tryAddMultiMonitorAndSourceParameters } from '../../../utilities/url';
import { ConfirmationDialogProperties } from '../../common/confirmation-dialog/contexts';
import { MetadataItemViewModel } from '../../common/metadata/models';
import { CustomizationDetailsPanelContextProperties } from '../../customization-details-panel/contexts';
import { ErrorDetailsPanelContextProperties } from '../../error-details-panel/contexts';
import { ConnectWithVSCodeDialogContextProperties } from '../../quick-action-dialogs/connect-with-vscode-dialog/connect-with-vscode-dialog-context';
import ResourceCard from '../../resource-card/resource-card';
import { ResourceCardAction } from '../../resource-card/resource-card-action';
import { OpenConnectViaAppDialogProperties } from '../../user-settings/connect-via-app-dialog/contexts';
import { getIsWelcomeTourDialogDismissed } from '../../welcome-tour-dialog/selectors';
import { DelayShutdownDialogProperties } from '../delay-shutdown-dialog/contexts';
import { DevBoxDetailsPanelContextProperties } from '../dev-box-details-panel/context';
import { DevBoxSupportPanelContextProperties } from '../dev-box-support-panel/context';
import { KeepDevBoxDialogProperties } from '../keep-devbox-dialog/context';
import { DevBoxViewModel } from '../models';
import { RepairDialogProperties } from '../repair-dialog/context';
import { RestoreSnapshotDialogProperties } from '../restore-snapshot-dialog/contexts';
import {
    getIsConnectViaAppRemoteDesktopDeprecatedTeachBubbleDismissed,
    getIsConnectViaAppTeachBubbleDismissed,
    getIsConnectViaAppTeachingPopoverDismissed,
    getIsConnectViaAppTeachingPopoverForWelcomeTourDismissed,
    getIsConnectViaAppTeachingPopoverForWelcomeTourSkipped,
    getIsConnectVSCodeDialogDismissed,
    getIsDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismissed,
} from '../selectors';
import { TakeSnapshotDialogProperties } from '../take-snapshot/context';
import { DevBoxCardActions } from './dev-box-card-actions';
import { getDevBoxStateMessage, shimmeredDevBoxMetadata } from './dev-box-card-metadata';
import { RepairOperationResultOutcome } from './models';
import {
    getAreActionsDisabled,
    getDevBoxCardBannerViewModel,
    getDevBoxCardMetadata,
    getInfoBannerIconProps,
    getPrimaryActions,
    getRepairDismissableContentId,
    getResourceCardError,
    getScheduleShutdownMetadataViewModel,
    getSecondaryActions,
    getUseTranslucentPreviewImage,
    isLinuxEnabled,
    shouldDisplayCustomizationFailure,
    shouldShowFirstTeachingPopoverForWelcomeTour,
    shouldShowSecondTeachingPopoverForWelcomeTour,
    showConnectViaAppTeachingBubble,
    showConnectWithVSCodeTeachingPopover,
} from './selectors';
import TeachingPopoversForDevboxCard from './teaching-popovers';
import WindowsProductImage from './windows-product-image';

interface DevBoxCardProps {
    devBox: DevBoxViewModel;
    locale: string;
    onDeleteSubmitted: (resourceId: string) => void;
    onGetRemoteConnectionSubmitted: (resourceId: string) => void;
    onOpenErrorPanel: (props: ErrorDetailsPanelContextProperties) => void;
    onOpenDevBoxDetailsPanel: (props: DevBoxDetailsPanelContextProperties) => void;
    onOpenDevBoxSupportPanel: (props: DevBoxSupportPanelContextProperties) => void;
    onStartSubmitted: (resourceId: string) => void;
    onEnableWindowsTunnelSubmitted: (resourceId: string) => void;
    onDelayShutdownSubmitted: (devBoxId: string, delayUntil: Date) => void;
    onSkipShutdownSubmitted: (devBoxId: string) => void;
    onOpenDelayShutdownDialog: (properties: DelayShutdownDialogProperties) => void;
    onOpenRestoreSnapshotDialog: (properties: RestoreSnapshotDialogProperties) => void;
    onOpenConnectViaAppDialog: (properties: OpenConnectViaAppDialogProperties) => void;
    onOpenConnectWithVSCodeDialog: (properties: ConnectWithVSCodeDialogContextProperties) => void;
    onOpenConfirmationDialog: (properties: ConfirmationDialogProperties) => void;
    onHibernateSubmitted: (resourceId: string) => void;
    onShutdownSubmitted: (resourceId: string) => void;
    onResumeSubmitted: (resourceId: string) => void;
    onRestartSubmitted: (resourceId: string) => void;
    onRepairSubmitted: (resourceId: string) => void;
    onTakeSnapshotSubmitted: (resourceId: string) => void;
    onRestoreSubmitted: (resourceId: string, snapshotId: string) => void;
    onOpenRepairDialog: (props: RepairDialogProperties) => void;
    onOpenTakeSnapshotDialog: (props: TakeSnapshotDialogProperties) => void;
    onOpenCustomizationPanel: (props: CustomizationDetailsPanelContextProperties) => void;
    onConnectViaAppTeachBubbleDismiss: () => void;
    onConnectViaAppTeachingPopoverForWelcomeTourDismiss: () => void;
    onConnectWithVSCodeTeachingPopoverDismiss: () => void;
    onDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismiss: () => void;
    onConnectViaAppTeachingPopoverForWelcomeTourSkipped: () => void;
    shouldShowConnectViaAppTeachBubble: boolean;
    shouldShowConnectViaAppTeachingPopoverForWelcomeTour: boolean;
    shouldShowDevboxSecondaryActionsTeachingPopoverForWelcomeTour: boolean;
    onSkipScheduledDeleteSubmitted: (devBoxId: string) => void;
    onOpenKeepDevBoxDialog: (properties: KeepDevBoxDialogProperties) => void;
}

const messages = defineMessages({
    deleteDialogPrimaryButtonAriaLabel: {
        id: 'DevBoxCard_DeleteDialogPrimaryButton_AriaLabel',
        defaultMessage: 'Delete the dev box',
        description: 'Aria label for the submit button to confirm deleting the dev box',
    },
    deleteDialogPrimaryButtonText: {
        id: 'DevBoxCard_DeleteDialogPrimaryButton_Text',
        defaultMessage: 'Delete',
        description: 'Confirmation button to delete the dev box',
    },
    deleteDialogSubText: {
        id: 'DevBoxCard_DeleteDialogSubText_Text',
        defaultMessage:
            'This workstation and all its data will be permanently removed. Are you sure you want to delete {name}?',
        description:
            'Informs the user about the effects of deleting the dev box and confirms. {name} should not be localized, it is the name of the dev box.',
    },
    deleteDialogTitle: {
        id: 'DevBoxCard_DeleteDialogTitle_Text',
        defaultMessage: 'Delete dev box',
        description: 'Title text for "Delete dev box" dialog',
    },
    deleteMenuItemAriaLabel: {
        id: 'DevBoxCard_DeleteMenuItem_AriaLabel',
        defaultMessage: 'Delete dev box',
        description: 'Aria label for "Delete" contextual menu item in dev box card\'s settings button.',
    },
    delayShutdownMenuItemAriaLabel: {
        id: 'DevBoxCard_DelayMenuItem_AriaLabel',
        defaultMessage: 'Delay shutdown for dev box',
        description: 'Aria label for "Delay shutdown" contextual menu item in dev box card\'s settings button.',
    },
    restoreMenuItemAriaLabel: {
        id: 'DevBoxCard_RestoreMenuItem_AriaLabel',
        defaultMessage: 'Restore',
        description: 'Text for "Restore" contextual menu item in dev box card\'s settings button.',
    },
    openInBrowserMenuItemAriaLabel: {
        id: 'DevBoxCard_OpenInBrowserMenuItem_AriaLabel',
        defaultMessage: 'Open in browser',
        description: 'Aria label for "Open in Browser" button in split button.',
    },
    connectViaAppMenuItemAriaLabel: {
        id: 'DevBoxCard_ConnectViaAppMenuItem_AriaLabel',
        defaultMessage: 'Connect via app',
        description: 'Aria label for "Connect via app" option in the open split button for dev box cards',
    },
    hyperVLinuxInstructionsMenuItemAriaLabel: {
        id: 'DevBoxCard_HyperVLinuxMenuItem_AriaLabel',
        defaultMessage: 'Instructions to connect',
        description: 'Aria label for "Linux Instructions to connect" option for dev box cards',
    },
    connectWithVSCodeMenuItemAriaLabel: {
        id: 'DevBoxCard_ConnectWithVSCodeMenuItem_AriaLabel',
        defaultMessage: 'Connect with VS Code',
        description: 'Aria label for "Connect with VS Code" option for dev box card',
    },
    connectWithVSCodeForWebMenuItemAriaLabel: {
        id: 'DevBoxCard_ConnectWithVSCodeForWebMenuItem_AriaLabel',
        defaultMessage: 'Connect with VS Code for Web',
        description: 'Aria label for "Connect with VS Code for Web" option for dev box card',
    },
    startItemAriaLabel: {
        id: 'DevBoxCard_StartMenuItem_AriaLabel',
        defaultMessage: 'Start dev box',
        description: 'Aria label for "Start" contextual menu item in dev box card\'s settings button.',
    },
    devBoxDetailsItemAriaLabel: {
        id: 'DevBoxCard_DevBoxDetailsMenuItem_AriaLabel',
        defaultMessage: 'More info',
        description: 'Aria label for "More Info" contextual menu item in dev box card\'s settings button.',
    },
    devBoxSupportItemAriaLabel: {
        id: 'DevBoxCard_DevBoxSupportMenuItem_AriaLabel',
        defaultMessage: 'Support',
        description: 'Aria label for "Support" contextual menu item in dev box card\'s settings button.',
    },
    hibernateMenuItemAriaLabel: {
        id: 'DevBoxCard_HibernateMenuItem_AriaLabel',
        defaultMessage: 'Hibernate',
        description: 'Text for "Hibernate" contextual menu item in dev box card\'s settings button.',
    },
    resumeMenuItemAriaLabel: {
        id: 'DevBoxCard_ResumeMenuItem_AriaLabel',
        defaultMessage: 'Resume',
        description: 'Text for "Resume" contextual menu item in dev box card\'s settings button.',
    },
    shutdownMenuItemAriaLabel: {
        id: 'DevBoxCard_ShutdownMenuItem_AriaLabel',
        defaultMessage: 'Shut down',
        description: 'Text for "Shut down" contextual menu item in dev box card\'s settings button.',
    },
    restartItemAriaLabel: {
        id: 'DevBoxCard_RestartMenuItem_AriaLabel',
        defaultMessage: 'Restart dev box',
        description: 'Aria label for "Restart" contextual menu item in dev box card\'s settings button.',
    },
    repairItemAriaLabel: {
        id: 'DevBoxCard_RepairMenuItem_AriaLabel',
        defaultMessage: 'Troubleshoot & repair dev box',
        description: 'Aria label for "Troubleshoot & repair" contextual menu item in dev box card\'s settings button.',
    },
    takeSnapshotItemAriaLabel: {
        id: 'DevBoxCard_TakeSnapshotMenuItem_AriaLabel',
        defaultMessage: 'Take snapshot',
        description: 'Aria label for "Take Snapshot" contextual menu item in dev box card\'s settings button.',
    },
    spinnerAriaLabel: {
        id: 'DevBoxCard_Spinner_AriaLabel',
        defaultMessage: 'In progress operation on your dev box',
        description: 'Aria label for spinner tracking progress of dev box operation in dev box card',
    },
    customizationsItemAriaLabel: {
        id: 'DevBoxCard_CustomizationsMenuItem_AriaLabel',
        defaultMessage: 'Customizations',
        description: 'Aria label for "Customizations" contextual menu item in dev box card\'s settings button.',
    },
    buttonAriaLabel: {
        id: 'DevBoxCard_SecondaryActionsButton_AriaLabel',
        defaultMessage: '{resourceName} actions',
        description: 'Aria label for actions button.',
    },
    // VS Code Connection Toast Messages
    startingDevBoxForVSCodeConnection: {
        id: 'DevBoxCard_Toast_StartingDevBoxForVSCodeConnection',
        defaultMessage: 'Starting dev box...',
        description: 'Toast message shown when starting a dev box to enable VS Code connection',
    },
    enablingTunnelForVSCodeConnection: {
        id: 'DevBoxCard_Toast_EnablingTunnelForVSCodeConnection',
        defaultMessage: 'Establishing tunnel...',
        description: 'Toast message shown when enabling tunnel for VS Code connection',
    },
    tunnelEnabledConnectingToVSCode: {
        id: 'DevBoxCard_Toast_TunnelEnabledConnectingToVSCode',
        defaultMessage: 'Opened Visual Studio Code Web',
        description: 'Toast message shown when tunnel has been enabled and is connecting to VS Code',
    },
    tunnelErrorConnectingToVSCode: {
        id: 'DevBoxCard_Toast_TunnelErrorConnectingToVSCode',
        defaultMessage: 'Error happened when establishing tunnel',
        description: 'Defualt toast message shown when there is an error connecting to VS Code via tunnel',
    },
    tunnelErrorConnectingToVSCodeLoginRequired: {
        id: 'DevBoxCard_Toast_TunnelErrorConnectingToVSCodeLoginRequired',
        defaultMessage: 'Login required to establish tunnel, please log in to the Dev Box first to initialize',
        description: 'Toast message shown when login is required to connect to VS Code via tunnel',
    },
    tunnelNotEnabledError: {
        id: 'DevBoxCard_Toast_TunnelNotEnabledError',
        defaultMessage: 'Unable to establish tunnel. Please contact your administrator.',
        description: 'Toast message shown when the DevBoxTunnelNotEnabled error occurs',
    },
});

/**
 * Styles
 */

const useBadgeStyles = makeStyles({
    root: {
        width: '100%',
        justifyContent: 'space-between',
    },
});
/**
 * END Styles
 */

export const DevBoxCard: React.FC<DevBoxCardProps> = React.memo((props: DevBoxCardProps) => {
    const {
        devBox,
        locale,
        onDeleteSubmitted,
        onGetRemoteConnectionSubmitted,
        onOpenErrorPanel,
        onOpenDevBoxDetailsPanel,
        onStartSubmitted,
        onEnableWindowsTunnelSubmitted,
        onDelayShutdownSubmitted,
        onSkipShutdownSubmitted,
        onOpenDelayShutdownDialog,
        onOpenConnectViaAppDialog,
        onOpenConnectWithVSCodeDialog,
        onOpenConfirmationDialog,
        onHibernateSubmitted,
        onShutdownSubmitted,
        onResumeSubmitted,
        onRestartSubmitted,
        onRepairSubmitted,
        onTakeSnapshotSubmitted,
        onRestoreSubmitted,
        onOpenRepairDialog,
        onOpenTakeSnapshotDialog,
        onOpenRestoreSnapshotDialog,
        onOpenCustomizationPanel,
        onOpenDevBoxSupportPanel,
        onConnectViaAppTeachBubbleDismiss,
        onConnectViaAppTeachingPopoverForWelcomeTourDismiss,
        onDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismiss,
        onConnectViaAppTeachingPopoverForWelcomeTourSkipped,
        onConnectWithVSCodeTeachingPopoverDismiss,
        shouldShowConnectViaAppTeachBubble,
        shouldShowConnectViaAppTeachingPopoverForWelcomeTour,
        shouldShowDevboxSecondaryActionsTeachingPopoverForWelcomeTour,
        onSkipScheduledDeleteSubmitted,
        onOpenKeepDevBoxDialog,
    } = props;

    const {
        cloudPcConnectionUrl,
        resource,
        state,
        webUrl,
        isCardContentReady,
        devBoxActionState,
        customizationGroup,
        customizationGroups,
        repairOperation,
        failureOnDevBox,
        customizationFailure,
        pool,
        projectDisplayName,
        snapshots,
        devBoxAddOns,
    } = devBox;

    const { result: repairOperationResult } = repairOperation ?? {};

    const customizationGroupStatus = React.useMemo(
        () => getOverallCustomizationGroupStatus(customizationGroups),
        [customizationGroups]
    );

    const waitingForUserSession = React.useMemo(
        () => isCustomizationGroupsWaitingForUserSession(customizationGroups),
        [customizationGroups]
    );

    const latestCustomizationGroupFailureEndTime = React.useMemo(
        () => getLatestCustomizationGroupFailureEndTime(customizationGroups),
        [customizationGroups]
    );

    const userRemoteAppOption = useSelector(getRemoteAppOptionsSelector);
    const userUseMultiMonitor = useSelector(getUseMultiMonitorSelector);
    const isConnectViaAppTeachBubbleDismissed = useSelector(getIsConnectViaAppTeachBubbleDismissed);
    const isConnectViaAppRemoteDesktopDeprecatedTeachBubbleDismissed = useSelector(
        getIsConnectViaAppRemoteDesktopDeprecatedTeachBubbleDismissed
    );
    const isConnectViaAppTeachingPopoverForWelcomeTourDismissed = useSelector(
        getIsConnectViaAppTeachingPopoverForWelcomeTourDismissed
    );

    const isConnectWithVSCodeTeachingPopoverDismissed = useSelector(getIsConnectViaAppTeachingPopoverDismissed);

    const isDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismissed = useSelector(
        getIsDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismissed
    );

    const isConnectViaAppTeachingPopoverForWelcomeTourSkipped = useSelector(
        getIsConnectViaAppTeachingPopoverForWelcomeTourSkipped
    );

    const {
        name,
        projectName: projectResourceName,
        poolName,
        location,
        createdTime,
        imageReference,
        hardwareProfile,
        storageProfile,
        hibernateSupport,
        uri,
        uniqueId,
    } = resource;

    const { originalScheduledStopTime, nextScheduledStopTime, skipState, delayState } = devBoxActionState;

    // Tunnel phase selectors for VS Code connection monitoring (optimized with combined selector)
    const enableDevBoxTunnelPhases = useSelector(getDevBoxTunnelPhaseStates(uri));

    // Check if Connect in VS Code dialog has been dismissed
    const isConnectVSCodeDialogDismissed = useSelector(getIsConnectVSCodeDialogDismissed);

    const connectTriggeredRef = React.useRef(false);
    const lastShownPhaseRef = React.useRef<DevBoxTunnelPhase | undefined>(undefined);

    const shouldEnableLinux = isLinuxEnabled(resource);

    const isConnectWithVSCodeEnabled = isFeatureFlagEnabled(FeatureFlagName.EnableConnectWithVSCode);

    const isWindowsDevBox = !shouldEnableLinux;

    // If it is windows devbox and connect with vs code is enabled - find the tunnel object(only one is expected).
    const windowsTunnel = React.useMemo(() => {
        if (!isConnectWithVSCodeEnabled || !isWindowsDevBox || !devBoxAddOns) {
            return undefined;
        } else {
            return findPrimaryTunnelAddon(devBoxAddOns);
        }
    }, [isWindowsDevBox, devBoxAddOns, isConnectWithVSCodeEnabled]);

    const windowsTunnelConnectionEnabled = React.useMemo(() => {
        return isConnectWithVSCodeEnabled && isWindowsDevBox;
    }, [isConnectWithVSCodeEnabled, isWindowsDevBox]);

    const tunnelCanBeConnected = React.useMemo(() => {
        return windowsTunnel ? getTunnelCanBeConnected(windowsTunnel) : false;
    }, [windowsTunnel]);

    // Toast functionality using global toast system
    const { showSuccessToast, showErrorToast, showInProgressToast } = useToast();

    // Theme hooks
    const theme = useCurrentFluent2Theme();

    // Styling hooks
    const badgeStyles = useBadgeStyles();
    const horizontalStackStyles = useHorizontalStackStyles();

    // Intl hooks
    const { formatMessage } = useIntl();

    // Note: putting these memoizers here since they're used within callbacks
    const hasHibernateSupport = hibernateSupport === HibernateSupport.Enabled;

    // Connect via app teach bubble element to anchor the bubble to
    const connectViaAppMenuItemAriaLabel = React.useMemo(() => {
        return `[aria-label="${formatMessage(messages.connectViaAppMenuItemAriaLabel)}"]`;
    }, [formatMessage]);

    const connectViaAppTeachBubbleTargetElement = document.querySelector(connectViaAppMenuItemAriaLabel);

    // Dev box secondary actions teach popover element to anchor the popover to
    const devBoxCardSecondaryActionsAriaLabel = React.useMemo(() => {
        return `[aria-label="${formatMessage(messages.buttonAriaLabel, { resourceName: name })}"]`;
    }, [formatMessage, name]);

    const devBoxCardSecondaryActionsTargetElement = document.querySelector(devBoxCardSecondaryActionsAriaLabel);

    const resourceCardError = React.useMemo(
        () =>
            getResourceCardError(
                failureOnDevBox,
                state,
                hasHibernateSupport,
                devBoxActionState,
                repairOperationResult,
                customizationGroupStatus,
                customizationFailure,
                latestCustomizationGroupFailureEndTime
            ),
        [
            failureOnDevBox,
            state,
            devBoxActionState,
            hasHibernateSupport,
            repairOperationResult,
            customizationGroupStatus,
            customizationFailure,
            latestCustomizationGroupFailureEndTime,
        ]
    );

    // If there are issues detected but could not be automatically fixed by repair operation, it is considered a failure
    const showSeeDetailsButton =
        !!resourceCardError?.failure ||
        repairOperationResult?.repairOutcome === RepairOperationResultOutcome.IssuesDetected ||
        shouldDisplayCustomizationFailure(customizationGroupStatus, latestCustomizationGroupFailureEndTime);

    const repairDismissableContentId = React.useMemo(
        () => getRepairDismissableContentId(uri, repairOperation?.operationId),
        [uri, repairOperation?.operationId]
    );

    const infoBannerIconProps: IIconProps = React.useMemo(
        () => getInfoBannerIconProps(state, devBoxActionState, waitingForUserSession, customizationGroupStatus),
        [state, waitingForUserSession, customizationGroupStatus, devBoxActionState]
    );

    const scheduledStopTimeString = React.useMemo(
        () =>
            originalScheduledStopTime !== undefined
                ? getTimeStringFromDate(originalScheduledStopTime, locale)
                : undefined,
        [originalScheduledStopTime, locale]
    );

    const projectName = React.useMemo(
        () => (isNotUndefinedOrWhiteSpace(projectDisplayName) ? projectDisplayName : projectResourceName),
        [projectDisplayName, projectResourceName]
    );

    const isShimmered = !isCardContentReady;

    const dismissContent = useActionCreator(dismissContentActionCreator);

    // Callback hooks
    const onDismiss = React.useCallback(() => {
        if (repairDismissableContentId) {
            dismissContent({ content: repairDismissableContentId });
        }
    }, [repairDismissableContentId, dismissContent]);

    const onDeleteDialogPrimaryButtonClicked = React.useCallback(() => {
        onDeleteSubmitted(uri);
    }, [onDeleteSubmitted, uri]);

    const onDevBoxDetailsMenuItemClicked = React.useCallback(() => {
        const props: DevBoxDetailsPanelContextProperties = {
            name,
            createdOn: createdTime,
            poolName,
            location,
            imageReference,
            hardwareProfile,
            storageProfile,
            scheduledStopTime: scheduledStopTimeString,
            hibernateSupport: hibernateSupport ?? HibernateSupport.Disabled,
        };

        onOpenDevBoxDetailsPanel(props);
    }, [
        name,
        createdTime,
        poolName,
        location,
        imageReference,
        hardwareProfile,
        storageProfile,
        scheduledStopTimeString,
        onOpenDevBoxDetailsPanel,
        hibernateSupport,
    ]);

    const onDevBoxSupportMenuItemClicked = React.useCallback(() => {
        const props: DevBoxSupportPanelContextProperties = {
            devBoxUniqueID: uniqueId ?? '',
            poolName,
            poolDisplayName: pool?.displayName,
            devBoxName: name,
            poolId: pool?.uri,
        };
        onOpenDevBoxSupportPanel(props);
    }, [uniqueId, name, pool?.displayName, poolName, pool?.uri]);

    const onOpenInBrowserSubmitted = React.useCallback(() => {
        if (isNotUndefinedOrWhiteSpace(webUrl)) {
            openInNewWindow(webUrl);
        }
    }, [webUrl]);

    const onOpenHyperVInstructionsSubmitted = React.useCallback(() => {
        openInNewWindow(HyperVDogfoodingInstructionsLink);
    }, []);

    const onOpenInAppSubmitted = React.useCallback(() => {
        if (isNotUndefinedOrWhiteSpace(cloudPcConnectionUrl)) {
            /**
             * Update the remote connection uri formatting based on user settings.
             * We need to add the usemultimon parameter to the uri and set the source from devportal.
             */
            const updatedRemoteDesktopConnection = tryAddMultiMonitorAndSourceParameters(
                cloudPcConnectionUrl,
                userUseMultiMonitor
            );

            if (isNotUndefinedOrWhiteSpace(updatedRemoteDesktopConnection)) {
                if (userRemoteAppOption) {
                    openInSameWindow(updatedRemoteDesktopConnection);
                    trackMetric(Metric.DirectLaunchStarted, 1, {
                        properties: {
                            [Property.MultiMonitor]: userUseMultiMonitor,
                            [Property.PreferredRemoteApp]: userRemoteAppOption,
                        },
                    });
                } else {
                    onOpenConnectViaAppDialog({ cloudPcConnectionUrl });
                }
            }
        }
    }, [onOpenConnectViaAppDialog, cloudPcConnectionUrl, userRemoteAppOption, userUseMultiMonitor]);

    const onConnectWithVSCodeSubmitted = React.useCallback(() => {
        if (isConnectVSCodeDialogDismissed) {
            // Dialog has been dismissed, connect directly
            const vscodeExtensionUri = `${VSCodeDevBoxExtensionWebHookPrefix}/${VSCodeConnectCommand}?${VSCodeDevBoxUriParameter}=${uri}`;
            window.open(vscodeExtensionUri, '_blank');
            trackMetric(Metric.ConnectWithVSCodeClicked, 1);
        } else {
            // Dialog has not been dismissed, show the dialog
            onOpenConnectWithVSCodeDialog({ devBoxUri: uri });
        }
    }, [isConnectVSCodeDialogDismissed, onOpenConnectWithVSCodeDialog, uri]);

    const onConnectWithVSCodeForWebSubmitted = React.useCallback(() => {
        // Trigger tunnel enabling - the tunnel phase monitoring will handle the toast messages
        // Reset phase tracking for new connection attempt
        connectTriggeredRef.current = true;
        lastShownPhaseRef.current = undefined;
        onEnableWindowsTunnelSubmitted(uri);
        trackMetric(Metric.ConnectWithVSCodeClicked, 1);
    }, [uri, onEnableWindowsTunnelSubmitted]);

    // Tunnel phase monitoring logic
    React.useEffect(() => {
        if (!connectTriggeredRef.current) {
            return; // Only run logic if triggered by onConnectWithVSCodeForWebSubmitted
        }

        let currentPhase: DevBoxTunnelPhase;

        if (enableDevBoxTunnelPhases.isInStartPhase) {
            currentPhase = DevBoxTunnelPhase.StartDevBox;

            // Only show toast if we haven't shown it for this phase yet
            if (lastShownPhaseRef.current !== currentPhase) {
                showInProgressToast(formatMessage(messages.startingDevBoxForVSCodeConnection), undefined, 0, name);
                lastShownPhaseRef.current = currentPhase;
            }
        } else if (enableDevBoxTunnelPhases.isInTunnelEnablingPhase) {
            currentPhase = DevBoxTunnelPhase.TunnelEnabling;

            // Only show toast if we haven't shown it for this phase yet
            if (lastShownPhaseRef.current !== currentPhase) {
                showInProgressToast(formatMessage(messages.enablingTunnelForVSCodeConnection), undefined, 0, name);
                lastShownPhaseRef.current = currentPhase;
            }
        } else if (enableDevBoxTunnelPhases.isTunnelEnablingFinished) {
            // If tunnel enabling finished: either handleSuccessfulConnection or display error message
            // Terminal phase - show toast and reset (no duplicate check needed since we reset immediately)
            if (state === DevBoxState.Running && tunnelCanBeConnected) {
                showSuccessToast(formatMessage(messages.tunnelEnabledConnectingToVSCode), undefined, 0, name);
                if (windowsTunnel?.codeTunnelUrl) {
                    openInNewWindow(windowsTunnel?.codeTunnelUrl);
                }
            } else {
                const errorMessage = isUserSessionNotAvailableForTunnelCode(windowsTunnel?.error?.code)
                    ? formatMessage(messages.tunnelErrorConnectingToVSCodeLoginRequired)
                    : formatMessage(messages.tunnelErrorConnectingToVSCode);
                showErrorToast(errorMessage, undefined, 0, name);
            }

            connectTriggeredRef.current = false; // Reset the ref after handling the finished state
            lastShownPhaseRef.current = undefined; // Reset for next connection attempt
        } else if (enableDevBoxTunnelPhases.isTunnelNotEnabled) {
            // If tunnel not enabled error: show specific error message
            // Terminal phase - show toast and reset (no duplicate check needed since we reset immediately)
            if (lastShownPhaseRef.current !== DevBoxTunnelPhase.TunnelNotEnabled) {
                showErrorToast(formatMessage(messages.tunnelNotEnabledError), undefined, 0, name);
            }

            connectTriggeredRef.current = false; // Reset the ref after handling the error state
            lastShownPhaseRef.current = undefined; // Reset for next connection attempt
        }
    }, [
        enableDevBoxTunnelPhases,
        state,
        tunnelCanBeConnected,
        windowsTunnel,
        showSuccessToast,
        showErrorToast,
        showInProgressToast,
        formatMessage,
        name,
    ]);

    const onRetryFailedOperation = React.useCallback(() => {
        const { reason } = failureOnDevBox ?? {};

        if (!reason) {
            return;
        }

        switch (reason) {
            case DevBoxFailureReason.DeleteFailed:
                onDeleteSubmitted(uri);
                return;
            case DevBoxFailureReason.GetRemoteConnectionFailed:
                onGetRemoteConnectionSubmitted(uri);
                return;
            case DevBoxFailureReason.HibernateFailed:
                onHibernateSubmitted(uri);
                return;
            case DevBoxFailureReason.RepairFailed:
                onRepairSubmitted(uri);
                return;
            case DevBoxFailureReason.TakeSnapshotFailed:
                onTakeSnapshotSubmitted(uri);
                return;
            case DevBoxFailureReason.RestartFailed:
                onRestartSubmitted(uri);
                return;
            case DevBoxFailureReason.ResumeFailed:
                onResumeSubmitted(uri);
                return;
            case DevBoxFailureReason.ShutdownFailed:
                onShutdownSubmitted(uri);
                return;
            case DevBoxFailureReason.StartFailed:
                onStartSubmitted(uri);
                return;
            default:
                trackTrace(`Invalid state: retry not currently supported after failure reason "${reason}".`, {
                    severity: Severity.Warning,
                });
                return;
        }
    }, [
        failureOnDevBox?.reason,
        onDeleteSubmitted,
        onGetRemoteConnectionSubmitted,
        onStartSubmitted,
        onHibernateSubmitted,
        onShutdownSubmitted,
        uri,
    ]);

    const onSeeErrorDetailsClicked = React.useCallback(() => {
        if (!showSeeDetailsButton) {
            trackTrace('Invalid state: error details requested, but failure message is falsy or white space.', {
                severity: Severity.Warning,
            });
            return;
        }

        // Makes sure we open customization details panel on customization group failure
        if (isFailedCustomizationGroupStatus(customizationGroupStatus) && customizationGroups && customizationGroup) {
            onOpenCustomizationPanel({ customizationGroup, customizationGroups, devBoxName: name });
            return;
        }

        onOpenErrorPanel({ failure: resourceCardError?.failure, devBoxName: name, repairOperationResult });
    }, [
        resourceCardError,
        name,
        showSeeDetailsButton,
        customizationGroupStatus,
        customizationGroup,
        customizationGroups,
        onOpenErrorPanel,
        onOpenCustomizationPanel,
    ]);

    const onSeeCustomizationDetailsClicked = React.useCallback(() => {
        if (!customizationGroups || !customizationGroup) {
            trackTrace(
                'Unexpected state: tried to open customization group details, but no customizationGroup was defined.',
                { severity: Severity.Warning }
            );
            return;
        }

        onOpenCustomizationPanel({
            customizationGroup,
            customizationGroups,
            devBoxName: name,
        });
    }, [name, customizationGroup, customizationGroups, onOpenCustomizationPanel]);

    const onStartMenuItemClicked = React.useCallback(() => {
        onStartSubmitted(uri);
    }, [onStartSubmitted, uri]);

    const onShutdownMenuItemClicked = React.useCallback(() => {
        onShutdownSubmitted(uri);
    }, [onShutdownSubmitted, uri]);

    const onHibernateMenuItemClicked = React.useCallback(() => {
        onHibernateSubmitted(uri);
    }, [onHibernateSubmitted, uri]);

    const onResumeMenuItemClicked = React.useCallback(() => {
        onResumeSubmitted(uri);
    }, [onResumeSubmitted, uri]);

    const onRestartMenuItemClicked = React.useCallback(() => {
        onRestartSubmitted(uri);
    }, [onRestartSubmitted, uri]);

    const onRepairDialogSubmitted = React.useCallback(() => {
        onRepairSubmitted(uri);
    }, [onRepairSubmitted, uri]);

    const onRepairMenuItemClicked = React.useCallback(() => {
        onOpenRepairDialog({ onRepairSubmitted: onRepairDialogSubmitted });
    }, [onOpenRepairDialog, onRepairDialogSubmitted]);

    const onTakeSnapshotDialogSubmitted = React.useCallback(() => {
        onTakeSnapshotSubmitted(uri);
    }, [onTakeSnapshotSubmitted, uri]);

    const onTakeSnapshotMenuItemClicked = React.useCallback(() => {
        onOpenTakeSnapshotDialog({
            snapshots,
            locale,
            onTakeSnapshotSubmitted: onTakeSnapshotDialogSubmitted,
        });
    }, [onOpenTakeSnapshotDialog, locale, snapshots, onTakeSnapshotDialogSubmitted]);

    const onSkipScheduledDeleteDialogSubmitted = React.useCallback(() => {
        onSkipScheduledDeleteSubmitted(uri);
    }, [onSkipScheduledDeleteSubmitted, uri]);

    const onSkipScheduledDeleteButtonClicked = React.useCallback(() => {
        onOpenKeepDevBoxDialog({
            onSkipScheduledDelete: onSkipScheduledDeleteDialogSubmitted,
        });
    }, [onOpenKeepDevBoxDialog, onSkipScheduledDeleteDialogSubmitted]);

    // We need both these values to calculate options for the skip / delay form
    const canDelayShutdown = !!nextScheduledStopTime && !!originalScheduledStopTime;

    const onRestoreMenuItemClicked = React.useCallback(() => {
        onOpenRestoreSnapshotDialog({
            devBoxId: uri,
            snapshots,
            locale,
            onRestoreSubmitted,
        });
    }, [onOpenRestoreSnapshotDialog, uri, locale, snapshots, onRestoreSubmitted]);

    const onDelayShutdownMenuItemClicked = React.useCallback(() => {
        if (canDelayShutdown) {
            onOpenDelayShutdownDialog({
                devBoxId: uri,
                locale,
                originalScheduledTime: originalScheduledStopTime,
                nextScheduledTime: nextScheduledStopTime,
                skipState,
                delayState,
                onDelaySubmitted: onDelayShutdownSubmitted,
                onSkipSubmitted: onSkipShutdownSubmitted,
                hasHibernateSupport,
            });
        }
    }, [
        onOpenDelayShutdownDialog,
        uri,
        locale,
        originalScheduledStopTime,
        nextScheduledStopTime,
        skipState,
        delayState,
        onDelayShutdownSubmitted,
        onSkipShutdownSubmitted,
        canDelayShutdown,
    ]);

    // Memoized data
    const arePrimaryActionsDisabled = React.useMemo(() => getAreActionsDisabled(state), [state]);
    const deleteDialogSubTextValues = React.useMemo(() => ({ name }), [name]);
    const useTranslucentPreviewImage = React.useMemo(() => getUseTranslucentPreviewImage(state), [state]);

    const onDeleteMenuItemClicked = React.useCallback(() => {
        onOpenConfirmationDialog({
            onPrimaryButtonSubmitted: onDeleteDialogPrimaryButtonClicked,
            primaryButtonAriaLabel: formatMessage(messages.deleteDialogPrimaryButtonAriaLabel),
            primaryButtonText: formatMessage(messages.deleteDialogPrimaryButtonText),
            subText: formatMessage(messages.deleteDialogSubText, deleteDialogSubTextValues),
            title: formatMessage(messages.deleteDialogTitle),
        });
    }, [onOpenConfirmationDialog, onDeleteDialogPrimaryButtonClicked, deleteDialogSubTextValues]);

    // Resource card actions
    const deleteMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.deleteMenuItemAriaLabel)}
                onClick={onDeleteMenuItemClicked}
                icon={<Delete20Regular />}
                key="delete"
            >
                <FormattedMessage
                    id="DevBoxCard_DeleteMenuItem_Text"
                    defaultMessage="Delete"
                    description='Text for "Delete" contextual menu item in dev box cards settings button.'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onDeleteMenuItemClicked]
    );

    const openInBrowserMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.openInBrowserMenuItemAriaLabel)}
                onClick={onOpenInBrowserSubmitted}
                icon={isConnectWithVSCodeEnabled ? undefined : <Open20Regular />}
                key="open-in-browser"
            >
                <FormattedMessage
                    id="DevBoxCard_OpenInBrowserMenuItem_Text"
                    defaultMessage="Open in browser"
                    description='Text for "Open in Browser" button in dev box card.'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onOpenInBrowserSubmitted]
    );

    const openHyperVInstructionsMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.hyperVLinuxInstructionsMenuItemAriaLabel)}
                onClick={onOpenHyperVInstructionsSubmitted}
                icon={<Open20Regular />}
                key="open-linux-instructions"
            >
                <FormattedMessage
                    id="DevBoxCard_OpenHyperVInstructionsMenuItem_Text"
                    defaultMessage="Instructions to connect"
                    description='Text for "Instructions to connect" button in dev box card.'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onOpenHyperVInstructionsSubmitted]
    );

    const openInAppMenuItem = React.useMemo(() => {
        return (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.connectViaAppMenuItemAriaLabel)}
                onClick={onOpenInAppSubmitted}
                icon={isConnectWithVSCodeEnabled ? undefined : <Laptop20Regular />}
                key="connect-via-app"
            >
                <FormattedMessage
                    id="DevBoxCard_ConnectViaAppMenuItem_Text"
                    defaultMessage="Connect via app"
                    description='Text for "Connect via app" option in the primary action split button for dev box cards'
                />
            </ResourceCardAction>
        );
    }, [formatMessage, onOpenInAppSubmitted, isConnectWithVSCodeEnabled]);

    const connectWithVSCodeMenuItem = React.useMemo(() => {
        return (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.connectWithVSCodeMenuItemAriaLabel)}
                onClick={onConnectWithVSCodeSubmitted}
                key="connect-via-vscode"
            >
                <FormattedMessage
                    id="DevBoxCard_ConnectWithVSCodeMenuItem_Text"
                    defaultMessage="Connect with VS Code"
                    description='Text for "Connect with VS Code" option for dev box card'
                />
            </ResourceCardAction>
        );
    }, [formatMessage, onConnectWithVSCodeSubmitted]);

    const connectWithVSCodeViaWebMenuItem = React.useMemo(() => {
        return (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.connectWithVSCodeForWebMenuItemAriaLabel)}
                onClick={onConnectWithVSCodeForWebSubmitted}
                key="connect-via-vscode-for-web"
            >
                <FormattedMessage
                    id="DevBoxCard_ConnectWithVSCodeForWebMenuItem_Text"
                    defaultMessage="Connect with VS Code for Web"
                    description='Text for "Connect with VS Code for Web" option for dev box card'
                />
            </ResourceCardAction>
        );
    }, [formatMessage, onConnectWithVSCodeForWebSubmitted]);

    const startMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.startItemAriaLabel)}
                onClick={onStartMenuItemClicked}
                icon={<Play20Regular />}
                key="start"
            >
                <FormattedMessage
                    id="DevBoxCard_StartMenuItem_Text"
                    defaultMessage="Start"
                    description='Text for "Start" contextual menu item in dev box cards settings button.'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onStartMenuItemClicked]
    );

    const shutdownMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.shutdownMenuItemAriaLabel)}
                onClick={onShutdownMenuItemClicked}
                icon={<Power20Regular />}
                key="shutdown"
            >
                <FormattedMessage
                    id="DevBoxCard_ShutdownMenuItem_Text"
                    defaultMessage="Shut down"
                    description='Text for "Shut down" contextual menu item in dev box cards settings button.'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onShutdownMenuItemClicked]
    );

    const delayShutdownMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.delayShutdownMenuItemAriaLabel)}
                onClick={onDelayShutdownMenuItemClicked}
                icon={<Next20Regular />}
                key="delay"
            >
                <FormattedMessage
                    id="DevBoxCard_DelayShutdownMenuItem_Text"
                    defaultMessage="Delay scheduled shutdown"
                    description='Text for "Delay scheduled shutdown" contextual menu item in dev box cards settings button.'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onDelayShutdownMenuItemClicked]
    );

    const restoreMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.restoreMenuItemAriaLabel)}
                onClick={onRestoreMenuItemClicked}
                icon={<CalendarArrowCounterclockwise20Regular />}
                key="restore"
            >
                <FormattedMessage
                    id="DevBoxCard_RestoreMenuItem_Text"
                    defaultMessage="Restore"
                    description='Text for "Restore" contextual menu item in dev box cards settings button.'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onRestoreMenuItemClicked]
    );

    const hibernateMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.hibernateMenuItemAriaLabel)}
                onClick={onHibernateMenuItemClicked}
                icon={<Clock20Regular />}
                key="hibernate"
            >
                <div className={mergeClasses(horizontalStackStyles.root, badgeStyles.root)}>
                    <div className={horizontalStackStyles.item}>
                        <FormattedMessage
                            id="DevBoxCard_HibernateMenuItem_Text"
                            defaultMessage="Hibernate"
                            description='Text for "Hibernate" contextual menu item in dev box cards settings button.'
                        />
                    </div>
                </div>
            </ResourceCardAction>
        ),
        [formatMessage, onHibernateMenuItemClicked]
    );

    const resumeMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.resumeMenuItemAriaLabel)}
                onClick={onResumeMenuItemClicked}
                icon={<Play20Regular />}
                key="resume"
            >
                <FormattedMessage
                    id="DevBoxCard_ResumeMenuItem_Text"
                    defaultMessage="Resume"
                    description='Text for "Resume" contextual menu item in dev box cards settings button.'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onResumeMenuItemClicked]
    );

    const restartMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.restartItemAriaLabel)}
                onClick={onRestartMenuItemClicked}
                icon={<ArrowClockwise20Regular />}
                key="restart"
            >
                <FormattedMessage
                    id="DevBoxCard_RestartMenuItem_Text"
                    defaultMessage="Restart"
                    description='Text for "Restart" contextual menu item in dev box cards settings button'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onRestartMenuItemClicked]
    );

    const devBoxDetailsMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.devBoxDetailsItemAriaLabel)}
                onClick={onDevBoxDetailsMenuItemClicked}
                icon={<Info20Regular />}
                key="more-info"
            >
                <FormattedMessage
                    id="DevBoxCard_DevBoxDetailsItem_Text"
                    defaultMessage="More Info"
                    description='Text for "More Info" contextual menu item in dev box cards settings button.'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onDevBoxDetailsMenuItemClicked]
    );

    const devBoxSupportMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.devBoxSupportItemAriaLabel)}
                onClick={onDevBoxSupportMenuItemClicked}
                icon={<Headset20Regular />}
                key="support"
            >
                <FormattedMessage
                    id="DevBoxCard_DevBoxSupportMenuItem_Text"
                    defaultMessage="Support"
                    description='Text for "Support" contextual menu item in dev box cards settings button.'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onDevBoxSupportMenuItemClicked]
    );

    const repairMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.repairItemAriaLabel)}
                onClick={onRepairMenuItemClicked}
                icon={<Wrench20Regular />}
                key="repair"
            >
                <FormattedMessage
                    id="DevBoxCard_RepairMenuItem_Text"
                    defaultMessage="Troubleshoot & repair"
                    description='Text for "Troubleshoot & repair" contextual menu item in dev box cards settings button'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onRepairMenuItemClicked]
    );

    const takeSnapshotMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.takeSnapshotItemAriaLabel)}
                onClick={onTakeSnapshotMenuItemClicked}
                icon={<Camera20Regular />}
                key="takeSnapshot"
            >
                <FormattedMessage
                    id="DevBoxCard_TakeSnapshotMenuItem_Text"
                    defaultMessage="Take snapshot"
                    description='Text for "Take Snapshot" contextual menu item in dev box cards settings button'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onTakeSnapshotMenuItemClicked]
    );

    const customizeMenuItem = React.useMemo(
        () => (
            <ResourceCardAction
                ariaLabel={formatMessage(messages.customizationsItemAriaLabel)}
                onClick={onSeeCustomizationDetailsClicked}
                icon={<TaskListLtr20Regular />}
                key="customization"
            >
                <FormattedMessage
                    id="DevBoxCard_CustomizationsMenuItem_Text"
                    defaultMessage="Customizations"
                    description='Text for "Customizations" contextual menu item in dev box cards settings button'
                />
            </ResourceCardAction>
        ),
        [formatMessage, onSeeCustomizationDetailsClicked]
    );

    const primaryActions = React.useMemo(
        () =>
            getPrimaryActions(
                openInBrowserMenuItem,
                openHyperVInstructionsMenuItem,
                openInAppMenuItem,
                operatingSystemFamily,
                state,
                shouldEnableLinux
            ),
        [openInBrowserMenuItem, openHyperVInstructionsMenuItem, openInAppMenuItem, state, shouldEnableLinux]
    );

    const headlessActions = React.useMemo(
        () =>
            windowsTunnelConnectionEnabled ? [connectWithVSCodeMenuItem, connectWithVSCodeViaWebMenuItem] : undefined,
        [windowsTunnelConnectionEnabled, connectWithVSCodeMenuItem, connectWithVSCodeViaWebMenuItem]
    );

    const devBoxCardActionsButton = React.useMemo(
        () => (
            <DevBoxCardActions
                primaryActions={primaryActions}
                headlessActions={headlessActions}
                arePrimaryActionsDisabled={arePrimaryActionsDisabled}
            />
        ),
        [primaryActions, headlessActions, arePrimaryActionsDisabled]
    );

    const hasCustomizationGroup = !!customizationGroups;

    const secondaryActions = React.useMemo(
        () =>
            getSecondaryActions(
                deleteMenuItem,
                startMenuItem,
                state,
                devBoxDetailsMenuItem,
                canDelayShutdown,
                delayShutdownMenuItem,
                restoreMenuItem,
                hasHibernateSupport,
                hibernateMenuItem,
                resumeMenuItem,
                shutdownMenuItem,
                restartMenuItem,
                repairMenuItem,
                takeSnapshotMenuItem,
                customizeMenuItem,
                hasCustomizationGroup,
                devBoxSupportMenuItem,
                shouldEnableLinux
            ),
        [
            deleteMenuItem,
            startMenuItem,
            state,
            devBoxDetailsMenuItem,
            delayShutdownMenuItem,
            restoreMenuItem,
            canDelayShutdown,
            hasHibernateSupport,
            hibernateMenuItem,
            resumeMenuItem,
            shutdownMenuItem,
            restartMenuItem,
            repairMenuItem,
            takeSnapshotMenuItem,
            customizeMenuItem,
            hasCustomizationGroup,
            devBoxSupportMenuItem,
            shouldEnableLinux,
        ]
    );

    // Resource card metadata

    const scheduleMetadata: MetadataItemViewModel | undefined = React.useMemo(
        () => getScheduleShutdownMetadataViewModel(locale, formatMessage, nextScheduledStopTime, hasHibernateSupport),
        [nextScheduledStopTime, locale, formatMessage, hasHibernateSupport]
    );

    const stateMetadata: MetadataItemViewModel | undefined = React.useMemo(() => {
        const stateMessage = getDevBoxStateMessage(state);

        return !!stateMessage
            ? {
                  ...stateMessage,
                  description: formatMessage(stateMessage.description),
              }
            : undefined;
    }, [state, formatMessage]);

    const metadata = React.useMemo(
        () => getDevBoxCardMetadata(state, scheduleMetadata, stateMetadata),
        [scheduleMetadata, stateMetadata, state]
    );

    const cardBanner = React.useMemo(
        () =>
            getDevBoxCardBannerViewModel(
                devBox,
                locale,
                onSkipScheduledDeleteButtonClicked,
                infoBannerIconProps,
                formatMessage(messages.spinnerAriaLabel),
                resourceCardError,
                onRetryFailedOperation,
                showSeeDetailsButton ? onSeeErrorDetailsClicked : undefined,
                customizationGroups ? onSeeCustomizationDetailsClicked : undefined,
                onDismiss
            ),
        [
            devBox,
            locale,
            infoBannerIconProps,
            formatMessage,
            onDismiss,
            resourceCardError,
            onRetryFailedOperation,
            onSeeErrorDetailsClicked,
            showSeeDetailsButton,
            customizationGroups,
            onSeeCustomizationDetailsClicked,
            onSkipScheduledDeleteButtonClicked,
        ]
    );

    const isWelcomeTourSeenOrSkipped = useSelector(getWelcomeTourSeenStatus);

    // This means the tour was at least partially seen
    const isWelcomeTourDialogDismissed = useSelector(getIsWelcomeTourDialogDismissed);

    const shouldShowFirstTeachingPopover = shouldShowFirstTeachingPopoverForWelcomeTour(
        shouldShowConnectViaAppTeachingPopoverForWelcomeTour,
        isConnectViaAppTeachingPopoverForWelcomeTourDismissed,
        isConnectViaAppTeachingPopoverForWelcomeTourSkipped,
        isCardContentReady,
        connectViaAppTeachBubbleTargetElement,
        isWelcomeTourSeenOrSkipped,
        isWelcomeTourDialogDismissed
    );

    const shouldShowSecondTeachingPopover = shouldShowSecondTeachingPopoverForWelcomeTour(
        shouldShowFirstTeachingPopover,
        shouldShowDevboxSecondaryActionsTeachingPopoverForWelcomeTour,
        isDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismissed,
        devBoxCardSecondaryActionsTargetElement,
        isCardContentReady,
        isWelcomeTourSeenOrSkipped,
        isWelcomeTourDialogDismissed
    );

    const shouldShowConnectViaAppTeachingPopover = showConnectViaAppTeachingBubble(
        isCardContentReady,
        shouldShowConnectViaAppTeachBubble,
        isConnectViaAppTeachBubbleDismissed,
        isConnectViaAppRemoteDesktopDeprecatedTeachBubbleDismissed,
        connectViaAppTeachBubbleTargetElement,
        isWelcomeTourSeenOrSkipped,
        isWelcomeTourDialogDismissed,
        isConnectWithVSCodeEnabled,
        userRemoteAppOption ?? RemoteAppOption.useWindowsApp
    );

    const shouldShowConnectWithVSCodeTeachingPopover = showConnectWithVSCodeTeachingPopover(
        isCardContentReady,
        shouldShowConnectViaAppTeachBubble,
        isConnectWithVSCodeTeachingPopoverDismissed,
        connectViaAppTeachBubbleTargetElement,
        isWelcomeTourSeenOrSkipped,
        isWelcomeTourDialogDismissed,
        isConnectWithVSCodeEnabled
    );

    return (
        <FluentProvider theme={theme}>
            <TeachingPopoversForDevboxCard
                shouldShowFirstTeachingPopoverForWelcomeTour={shouldShowFirstTeachingPopover}
                shouldShowSecondTeachingPopoverForWelcomeTour={shouldShowSecondTeachingPopover}
                shouldShowConnectViaAppTeachingBubble={shouldShowConnectViaAppTeachingPopover}
                onConnectViaAppTeachBubbleDismiss={onConnectViaAppTeachBubbleDismiss}
                onConnectViaAppTeachingPopoverForWelcomeTourDismiss={
                    onConnectViaAppTeachingPopoverForWelcomeTourDismiss
                }
                onDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismiss={
                    onDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismiss
                }
                onConnectViaAppTeachingPopoverForWelcomeTourSkip={onConnectViaAppTeachingPopoverForWelcomeTourSkipped}
                devBoxCardSecondaryActionsTargetElement={devBoxCardSecondaryActionsTargetElement}
                connectViaAppTeachBubbleTargetElement={connectViaAppTeachBubbleTargetElement}
                shouldShowConnectWithVSCodeTeachingPopover={shouldShowConnectWithVSCodeTeachingPopover}
                onConnectWithVSCodeTeachingPopoverDismissed={onConnectWithVSCodeTeachingPopoverDismiss}
            />
            <ResourceCard
                metadata={metadata}
                name={name}
                productIcon={shouldEnableLinux ? <LinuxDogfoodingLogoIcon /> : <WindowsProductImage />}
                previewImageSrc={
                    shouldEnableLinux
                        ? devBoxCardPreviewLinuxImage
                        : theme === webLightTheme
                        ? devBoxCardPreviewWin11Image_Light
                        : devBoxCardPreviewWin11Image_Dark
                }
                footerContent={devBoxCardActionsButton}
                projectName={projectName}
                secondaryActions={secondaryActions}
                useTranslucentPreviewImage={useTranslucentPreviewImage}
                isShimmered={isShimmered}
                shimmeredMetadata={shimmeredDevBoxMetadata}
                cardBanner={cardBanner}
            />
        </FluentProvider>
    );
});

export default DevBoxCard;
